import { useEffect, useState, useRef } from "react";
import * as React from "react";
import { Form, Button, FormInstance } from "antd";

import CompanyIdentificationForm from "./CompanyIdentificationForm";
import DateAndCurrencyForm from "./DateAndCurrencyForm";
import FormulaBuilderForm from "../FormulaBuilderForm";
import { validateForm } from "../../../utils/validation";
import { buildFormula, FormulaType } from "../../../utils/formulaBuilder";
import { DateValue, CurrencyValue, DateSelectionType, CurrencySelectionType, CompanyIdentificationValue, CompanyIdentificationType, EquityPlaceholderName } from "../../../types/newLinkBuilder";
import NewLinkBuilerDataSelection from "./DataSelection";
import { DialogMessageType } from "../../../types/commands";
import { useStyles } from "../../style";

const NewLinkBuilderForm: React.FC = () => {
    const styles = useStyles();
    const [form] = Form.useForm();

    const [selectedDataPoint, setSelectedDataPoint] = useState<string | null>('Closing_Share_Price');
    const [isVolumeAndTrendingCurrencySelected, setIsVolumeAndTrendingCurrencySelected] = useState(false);
    const derivedMarketDataPoints = ['Period_High', 'Period_Low', 'Period_Median', 'Period_Average', 'Period_VWAP', 'Period_P25', 'Period_P75'];
    const showTimePeriod = selectedDataPoint === 'Trading_Days' || derivedMarketDataPoints.includes(selectedDataPoint || "");

    const [companyIdentification, setCompanyIdentification] = useState<CompanyIdentificationValue>({
        selected: CompanyIdentificationType.Search,
        searchInput: "",
        exchange: "",
        ticker: "",
    });

    const [date, setDate] = useState<DateValue>({
        selected: DateSelectionType.Date,
        date: "",
        dateCellReference: "",
    });

    const [currency, setCurrency] = useState<CurrencyValue>({
        selected: CurrencySelectionType.Value,
        currency: "Trading_Currency",
        currencyCellReference: "",
    });

    const [formulaLocation, setFormulaLocation] = useState<string>("");
    const [formula, setFormula] = useState<string>("");

    const companyIdentificationRef = useRef<HTMLDivElement>(null);
    const dateAndCurrencyRef = useRef<HTMLDivElement>(null);
    const formulaBuilderRef = useRef<HTMLDivElement>(null);
    const [scrollTarget, setScrollTarget] = useState<'company' | 'dateAndCurrency' | 'formula' | null>(null);

    useEffect(() => {
        form.setFieldsValue({
            companyIdentification: companyIdentification.selected,
            asOfDate: date.selected,
            currencyType: currency.selected,
            timePeriod: date.selected,
        });
    }, [companyIdentification.selected, date.selected, currency.selected]);

    useEffect(() => {
        const buildFormulaAsync = async () => {
            let formula = '';
            const formulaType = (selectedDataPoint && derivedMarketDataPoints.includes(selectedDataPoint)) ? FormulaType.DerivedMarketData: FormulaType.Equity;
            formula = await buildFormula(
                formulaType,
                {
                    selectedDataPoint,
                    companyIdentification,
                    date,
                    currency
                }
            ); 
            setFormula(formula);
        };
        buildFormulaAsync();
    }, [selectedDataPoint, companyIdentification, date, currency, formulaLocation]);

    useEffect(() => {
        if (scrollTarget === 'company') {
            companyIdentificationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setScrollTarget(null);
        } else if (scrollTarget === 'dateAndCurrency') {
            dateAndCurrencyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setScrollTarget(null);
        } else if (scrollTarget === 'formula') {
            formulaBuilderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setScrollTarget(null);
        }
    }, [scrollTarget]);

    useEffect(() => {
        const placeholder = localStorage.getItem("placeholder");
        const cellAddress = localStorage.getItem("cellAddress");
        const activeCell = localStorage.getItem("activeCell");

        if (activeCell) {
            setFormulaLocation(activeCell);
        }

        if (placeholder) {
            const placeholderData = JSON.parse(placeholder);

            setSelectedDataPoint(placeholderData.data.selectedDataPoint);
            setIsVolumeAndTrendingCurrencySelected(['Traded_Volume_Shares', 'Trading_Currency', 'Trading_Days'].includes(placeholderData.data.selectedDataPoint));
            setCompanyIdentification(placeholderData.data.companyIdentification);
            setDate(placeholderData.data.date);
            setCurrency(placeholderData.data.currency);
            setFormulaLocation(placeholderData.data.formulaLocation);

            switch (placeholderData.placeholderName) {
                case EquityPlaceholderName.CompanyIdentificationExchangeCellReference:
                    setCompanyIdentification(prev => ({
                        ...prev,
                        selected: CompanyIdentificationType.CellReference,
                        exchange: cellAddress,
                    }));
                    setScrollTarget('company');
                    break;
                case EquityPlaceholderName.CompanyIdentificationTickerCellReference:
                    setCompanyIdentification(prev => ({
                        ...prev,
                        selected: CompanyIdentificationType.CellReference,
                        ticker: cellAddress,
                    }));
                    setScrollTarget('company');
                    break;
                case EquityPlaceholderName.DateCellReference:
                    setDate(prev => ({
                        ...prev,
                        selected: DateSelectionType.CellReference,
                        dateCellReference: cellAddress,
                    }));
                    setScrollTarget('dateAndCurrency');
                    break;
                case EquityPlaceholderName.CurrencyCellReference:
                    setCurrency(prev => ({
                        ...prev,
                        selected: CurrencySelectionType.CellReference,
                        currencyCellReference: cellAddress,
                    }));
                    setScrollTarget('dateAndCurrency');
                    break;
                case EquityPlaceholderName.BeginningDateCellReference:
                    setDate(prev => ({
                        ...prev,
                        selected: DateSelectionType.CellReference,
                        beginDateCellReference: cellAddress,
                    }));
                    setScrollTarget('dateAndCurrency');
                    break;
                case EquityPlaceholderName.EndingDateCellReference:
                    setDate(prev => ({
                        ...prev,
                        selected: DateSelectionType.CellReference,
                        endDateCellReference: cellAddress,
                    }));
                    setScrollTarget('dateAndCurrency');
                    break;
                case EquityPlaceholderName.FormulaLocationCellReference:
                    setFormulaLocation(cellAddress);
                    setScrollTarget('formula');
                    break;
                default:
                    break;
            }
        }

        return () => {
            localStorage.removeItem("placeholder");
            localStorage.removeItem("cellAddress");
        }
    }, []);

    const handleOk = (form: FormInstance) => {
        const companyIdentificationError = validateForm(companyIdentification, form, "companyIdentification");
        const dateError = showTimePeriod ? false : validateForm(date, form, "asOfDate");
        const timePeriodError = showTimePeriod ? validateForm(date, form, "timePeriod") : false;
        const currencyError = isVolumeAndTrendingCurrencySelected ? false : validateForm(currency, form, "currencyType");
        const formulaLocationError = validateForm(formulaLocation, form, "formulaLocation");

        const hasError = companyIdentificationError || dateError || timePeriodError || currencyError || formulaLocationError;

        if (!hasError) {
            Office.context.ui.messageParent(JSON.stringify({
                action: DialogMessageType.ADD_LINK_TO_CELL,
                data: {
                    formula: formula,
                    address: formulaLocation,
                },
            }));
        }
    }

    const handleCancel = () => {
        Office.context.ui.messageParent(JSON.stringify({
            action: DialogMessageType.CLOSE,
        }));
    }

    const handlePlaceholder = (name: EquityPlaceholderName) => {
        const value = {
            placeholderName: name,
            data: {
                selectedDataPoint,
                companyIdentification,
                date,
                currency,
                formulaLocation,
                isVolumeAndTrendingCurrencySelected,
            }
        }

        localStorage.setItem("placeholder", JSON.stringify(value));
        localStorage.setItem("redirectTo", "equity-data");
        Office.context.ui.messageParent(JSON.stringify({
            action: DialogMessageType.OPEN_PLACEHOLDER,
        }));
    }

    const handleVolumeOrCurrencySelection = (isSelected: boolean) => {
        setIsVolumeAndTrendingCurrencySelected(isSelected);
    };

    return (
        <Form form={form}
            initialValues={{
                companyIdentification: CompanyIdentificationType.Search,
                asOfDate: DateSelectionType.Date,
                currencyType: CurrencySelectionType.Value,
                timePeriod: DateSelectionType.Date
            }}
            onValuesChange={(changedValues) => {
                if (changedValues.companyIdentification !== undefined) {
                    setCompanyIdentification(prev => ({
                        ...prev,
                        selected: changedValues.companyIdentification,
                        ...(changedValues.companyIdentification === CompanyIdentificationType.Search
                            ? { exchange: "", ticker: "" }
                            : { searchInput: "" })
                    }));
                }
                if (changedValues.asOfDate !== undefined) {
                    setDate(prev => ({
                        ...prev,
                        selected: changedValues.asOfDate,
                        ...(changedValues.asOfDate === DateSelectionType.Date
                            ? { dateCellReference: "" }
                            : { date: "" })
                    }));
                }

                if (changedValues.timePeriod !== undefined) {
                    setDate(prev => ({
                        ...prev,
                        selected: changedValues.timePeriod,
                        ...(changedValues.timePeriod === DateSelectionType.Date
                            ? { 
                                beginDateCellReference: "",
                                endDateCellReference: "",
                              }
                            : { 
                                beginDate: "",
                                endDate: "",
                              })
                    }));
                }

                if (changedValues.currencyType !== undefined) {
                    setCurrency(prev => ({
                        ...prev,
                        selected: changedValues.currencyType,
                        ...(changedValues.currencyType === CurrencySelectionType.Value
                            ? { currencyCellReference: "", currency: "Trading_Currency" }
                            : { currency: "" })
                    }));
                }
            }}
            className={styles.radio}
        >
            <NewLinkBuilerDataSelection
                selectedDataPoint={selectedDataPoint}
                setSelectedDataPoint={setSelectedDataPoint}
                onVolumeOrCurrencySelected={handleVolumeOrCurrencySelection}
            />
            <div ref={companyIdentificationRef}>
                <CompanyIdentificationForm value={companyIdentification} setValue={setCompanyIdentification} handlePlaceholder={handlePlaceholder} />
            </div>
            <div ref={dateAndCurrencyRef}>
                <DateAndCurrencyForm
                    dateValue={date}
                    setDateValue={setDate}
                    currencyValue={currency}
                    setCurrencyValue={setCurrency}
                    handlePlaceholder={handlePlaceholder}
                    isVolumeAndTrendingCurrencySelected={isVolumeAndTrendingCurrencySelected}
                    showTimePeriod={showTimePeriod}
                />
            </div>
            <div ref={formulaBuilderRef}>
                <FormulaBuilderForm formula={formula} formulaLocation={formulaLocation} setFormulaLocation={setFormulaLocation} handlePlaceholder={handlePlaceholder} />
            </div>
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '10px',
                marginBlock: '20px',
            }}>
                <Button type="primary" onClick={() => handleOk(form)}>Ok</Button>
                <Button onClick={handleCancel}>Cancel</Button>
            </div>
        </Form>
    );
};

export default NewLinkBuilderForm;
